import { useQueryClient } from '@tanstack/react-query';
import { ZfLoadingSpinner } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { Suspense, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useYwuApp } from '../../../app/contexts/ywu-app-context';
import { showErrorToast, showSuccessToast } from '../../../app/utils/toastUtils';
import { useWebComponentEvents } from '../../hooks';
import type { PartnerDTO, PreselectedRelationshipType, SourceRelation } from '../../types/PartnerRelation';
import { refetchFamilyTreeAfterRelationChange } from '../../utils/refetch-family-tree-after-relation-change';
import { PartnerRelationModalWidget } from './PcmPartnerRelationModalWidget';

interface ICreatePartnerRelationModalProps {
    partnerUuid: string;
    sourceRelations: SourceRelation[];
    availablePartners: PartnerDTO[];
    relationshipManagerUserId?: string;
    onModalClose: () => void;
    preselectedRelationshipType?: PreselectedRelationshipType;
}

export const CreatePartnerRelationModal: FunctionComponent<ICreatePartnerRelationModalProps> = ({
    partnerUuid,
    sourceRelations,
    preselectedRelationshipType,
    availablePartners,
    relationshipManagerUserId,
    onModalClose,
}) => {
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const { isPresentationMode } = useYwuApp();

    const modalRef = useRef<HTMLElement>(null);
    const [isModalReady, setIsModalReady] = useState<boolean>(false);

    useWebComponentEvents(modalRef, isModalReady, [
        {
            eventName: 'PCM_CREATE_PARTNER_RELATION_MODAL_CLOSE',
            eventCallback: () => {
                onModalClose();
                setIsModalReady(false);
            },
        },
        {
            eventName: 'PCM_CREATE_PARTNER_RELATION_SUCCESS',
            eventCallback: () => {
                showSuccessToast(t('createPartnerRelationSuccess'));
                refetchFamilyTreeAfterRelationChange(queryClient, partnerUuid);
            },
        },
        {
            eventName: 'PCM_CREATE_PARTNER_RELATION_ERROR',
            eventCallback: () => showErrorToast({ userMessage: t('createPartnerRelationFailure') }),
        },
    ]);

    return createPortal(
        <Suspense fallback={<ZfLoadingSpinner style={{ margin: 'auto' }} />}>
            <PartnerRelationModalWidget
                ref={modalRef}
                modalType="CreatePartnerRelation"
                language={i18n.language}
                isPresentationMode={isPresentationMode}
                sourceRelations={sourceRelations}
                preselectedRelationshipType={preselectedRelationshipType}
                availablePartners={availablePartners}
                relationshipManagerUserId={relationshipManagerUserId}
                onModalReady={() => setIsModalReady(true)}
            />
        </Suspense>,
        document.body,
    );
};
